import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
} from "react";
import {FlowData} from "@/types/node";
import {
  createDefaultFlowData,
  createMainFlowDefaultFlowData,
} from "@/utils/defaultFlowData";

export interface SubflowItem {
  id: string;
  name: string;
  description?: string;
  flowData: FlowData;
  createdAt: Date;
  updatedAt: Date;
}

interface SubflowsState {
  subflows: SubflowItem[];
  selectedSubflowId: string | null;
  mainFlow: SubflowItem; // Always present main flow
}

type SubflowsAction =
  | {type: "SET_SUBFLOWS"; payload: SubflowItem[]}
  | {type: "ADD_SUBFLOW"; payload: SubflowItem}
  | {
      type: "UPDATE_SUBFLOW";
      payload: {id: string; updates: Partial<SubflowItem>};
    }
  | {type: "REMOVE_SUBFLOW"; payload: string}
  | {type: "CLEAR_SUBFLOWS"}
  | {type: "DUPLICATE_SUBFLOW"; payload: string}
  | {type: "SELECT_SUBFLOW"; payload: string | null}
  | {type: "UPDATE_MAIN_FLOW"; payload: Partial<SubflowItem>}
  | {type: "INITIALIZE_MAIN_FLOW"; payload: SubflowItem};

interface SubflowsContextType {
  subflows: SubflowItem[];
  selectedSubflowId: string | null;
  selectedSubflow: SubflowItem | undefined;
  mainFlow: SubflowItem;
  getSubflowById: (id: string) => SubflowItem | undefined;
  getAllSubflows: () => SubflowItem[];
  addSubflow: (
    subflow: Omit<
      SubflowItem,
      "id" | "createdAt" | "updatedAt" | "flowData"
    > & {
      flowData?: FlowData;
    },
  ) => SubflowItem;
  updateSubflow: (id: string, updates: Partial<SubflowItem>) => void;
  removeSubflow: (id: string) => void;
  clearSubflows: () => void;
  duplicateSubflow: (id: string) => void;
  selectSubflow: (id: string | null) => void;
  selectMainFlow: () => void;
  updateCurrentSubflow: (updates: Partial<SubflowItem>) => void;
}

const SubflowsContext = createContext<SubflowsContextType | undefined>(
  undefined,
);

const subflowsReducer = (
  state: SubflowsState,
  action: SubflowsAction,
): SubflowsState => {
  let newState: SubflowsState;

  switch (action.type) {
    case "SET_SUBFLOWS":
      newState = {
        subflows: action.payload,
        selectedSubflowId: state.selectedSubflowId || "main", // Keep current selection or default to main
        mainFlow: state.mainFlow,
      };
      break;
    case "ADD_SUBFLOW":
      newState = {
        subflows: [...state.subflows, action.payload],
        selectedSubflowId: state.selectedSubflowId,
        mainFlow: state.mainFlow,
      };
      break;
    case "UPDATE_SUBFLOW":
      newState = {
        subflows: state.subflows.map(s =>
          s.id === action.payload.id
            ? {...s, ...action.payload.updates, updatedAt: new Date()}
            : s,
        ),
        selectedSubflowId: state.selectedSubflowId,
        mainFlow: state.mainFlow,
      };
      break;
    case "REMOVE_SUBFLOW":
      newState = {
        subflows: state.subflows.filter(s => s.id !== action.payload),
        selectedSubflowId:
          state.selectedSubflowId === action.payload
            ? null
            : state.selectedSubflowId,
        mainFlow: state.mainFlow,
      };
      break;
    case "CLEAR_SUBFLOWS":
      newState = {
        subflows: [],
        selectedSubflowId: state.selectedSubflowId || "main", // Keep current selection or default to main
        mainFlow: state.mainFlow,
      };
      break;
    case "SELECT_SUBFLOW":
      newState = {
        ...state,
        selectedSubflowId: action.payload,
      };
      break;
    case "UPDATE_MAIN_FLOW":
      newState = {
        ...state,
        mainFlow: {...state.mainFlow, ...action.payload, updatedAt: new Date()},
      };
      break;
    case "INITIALIZE_MAIN_FLOW":
      newState = {
        ...state,
        mainFlow: action.payload,
      };
      break;
    default:
      return state;
  }

  return newState;
};

export const SubflowsProvider: React.FC<{children: ReactNode}> = ({
  children,
}) => {
  const mainFlowDefaultData = createMainFlowDefaultFlowData();

  const [state, dispatch] = useReducer(subflowsReducer, {
    subflows: [],
    selectedSubflowId: "main", // Select main flow by default
    mainFlow: {
      id: "main",
      name: "Main Flow",
      description: "The main flow",
      flowData: mainFlowDefaultData,
      createdAt: new Date(),
      updatedAt: new Date(),
    },
  });

  const getSubflowById = (id: string) => {
    if (id === "main") {
      return state.mainFlow;
    }
    return state.subflows.find(s => s.id === id);
  };

  const getAllSubflows = () => {
    return state.subflows;
  };

  const addSubflow = (
    subflowData: Omit<
      SubflowItem,
      "id" | "createdAt" | "updatedAt" | "flowData"
    > & {
      flowData?: FlowData;
    },
  ): SubflowItem => {
    const now = new Date();

    // Generate default name if not provided
    const subflowCount = state.subflows.length;
    const defaultName = `Subflow ${subflowCount + 1}`;

    const newSubflow: SubflowItem = {
      ...subflowData,
      name: subflowData.name || defaultName,
      flowData: subflowData.flowData || createDefaultFlowData(),
      id: `subflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: now,
      updatedAt: now,
    };
    dispatch({type: "ADD_SUBFLOW", payload: newSubflow});
    return newSubflow;
  };

  const updateSubflow = (id: string, updates: Partial<SubflowItem>) => {
    // Handle main flow or regular subflow with one unified function
    if (id === "main") {
      dispatch({type: "UPDATE_MAIN_FLOW", payload: updates});
    } else {
      dispatch({type: "UPDATE_SUBFLOW", payload: {id, updates}});
    }
  };

  const removeSubflow = (id: string) => {
    dispatch({type: "REMOVE_SUBFLOW", payload: id});
  };

  const clearSubflows = () => {
    dispatch({type: "CLEAR_SUBFLOWS"});
  };

  const duplicateSubflow = (id: string) => {
    const subflow = getSubflowById(id);
    if (subflow) {
      const now = new Date();
      const duplicatedSubflow: SubflowItem = {
        ...subflow,
        id: `subflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: `${subflow.name} (Copy)`,
        createdAt: now,
        updatedAt: now,
      };
      dispatch({type: "ADD_SUBFLOW", payload: duplicatedSubflow});
    }
  };

  const selectSubflow = (id: string | null) => {
    dispatch({type: "SELECT_SUBFLOW", payload: id});
  };

  const selectMainFlow = () => {
    dispatch({type: "SELECT_SUBFLOW", payload: "main"});
  };

  const updateCurrentSubflow = (updates: Partial<SubflowItem>) => {
    if (state.selectedSubflowId) {
      updateSubflow(state.selectedSubflowId, updates);
    }
  };

  const selectedSubflow = state.selectedSubflowId
    ? getSubflowById(state.selectedSubflowId)
    : undefined;

  const value: SubflowsContextType = {
    subflows: state.subflows,
    selectedSubflowId: state.selectedSubflowId,
    selectedSubflow,
    mainFlow: state.mainFlow,
    getSubflowById,
    getAllSubflows,
    addSubflow,
    updateSubflow,
    removeSubflow,
    clearSubflows,
    duplicateSubflow,
    selectSubflow,
    selectMainFlow,
    updateCurrentSubflow,
  };

  // useEffect(() => {
  //   console.log("SubflowsContext state:", {
  //     selectedSubflowId: state.selectedSubflowId,
  //     subflowsCount: state.subflows.length,
  //     mainFlowId: state.mainFlow.id,
  //     mainFlowNodesCount: state.mainFlow.flowData.live.nodes.length,
  //     mainFlowName: state.mainFlow.name,
  //   });
  //   console.log("Regular subflows:", state.subflows);
  //   console.log("Main flow:", state.mainFlow);
  //   console.log("--------------------------------");
  // }, [state.selectedSubflowId, state.subflows, state.mainFlow]);

  return (
    <SubflowsContext.Provider value={value}>
      {children}
    </SubflowsContext.Provider>
  );
};

export const useSubflows = () => {
  const context = useContext(SubflowsContext);
  if (!context) {
    throw new Error("useSubflows must be used within a SubflowsProvider");
  }
  return context;
};
